import { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Plus, Trash2, Save, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const when = v => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const sel = 'h-8 w-full text-xs border border-slate-200 rounded-md px-2 bg-white';

const EmployeeRow = ({ e, i, opts, onChange, onRemove }) => (
  <div className="grid grid-cols-12 gap-2 items-center" data-testid={`isa-rules-emp-${i}`}>
    <Input className="col-span-3 h-8 text-xs" value={e.name} onChange={x => onChange({ ...e, name: x.target.value })} placeholder="Name" data-testid={`isa-rules-emp-name-${i}`} />
    <select className={`${sel} col-span-3`} value={e.emp_id} onChange={x => onChange({ ...e, emp_id: x.target.value })} data-testid={`isa-rules-emp-id-${i}`}>
      <option value="">Emp ID (Hot credit)…</option>
      {[...new Set([e.emp_id, ...(opts?.emp_ids || [])])].filter(Boolean).map(v => <option key={v} value={v}>{v}</option>)}
    </select>
    <select className={`${sel} col-span-3`} value={e.owner_id}
      onChange={x => { const u = opts?.users.find(y => y.id === x.target.value); onChange({ ...e, owner_id: x.target.value, owner_name: u?.name || '' }); }}
      data-testid={`isa-rules-emp-owner-${i}`}>
      <option value="">CRM owner (Call Back / UTR)…</option>
      {!(opts?.users || []).some(u => u.id === e.owner_id) && e.owner_id && <option value={e.owner_id}>{e.owner_name}</option>}
      {(opts?.users || []).map(u => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
    </select>
    <div className="col-span-2 flex items-center gap-1">
      <Input type="number" className="h-8 text-xs" value={e.share_pct} onChange={x => onChange({ ...e, share_pct: x.target.value })} data-testid={`isa-rules-emp-pct-${i}`} />
      <span className="text-xs text-slate-400">%</span>
    </div>
    <button onClick={onRemove} className="col-span-1 text-slate-400 hover:text-rose-600 justify-self-center" aria-label="Remove employee" data-testid={`isa-rules-emp-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
  </div>
);

const History = ({ history }) => (
  <Card className="p-4" data-testid="isa-rules-history">
    <h3 className="text-sm font-bold text-slate-900 mb-2">Change history</h3>
    <div className="space-y-3 max-h-[520px] overflow-y-auto">
      {history.map(v => (
        <div key={v.id || v.version} className="text-xs border-b border-slate-50 pb-2" data-testid={`isa-rules-version-${v.version}`}>
          <div className="text-slate-900 font-semibold">Version {v.version} · from {v.effective_from}</div>
          <div className="text-[10px] text-slate-400">{v.created_by} · {when(v.created_at)}</div>
          {v.note && <div className="text-slate-600 mt-0.5">“{v.note}”</div>}
          {v.backdated && <div className="text-amber-700 text-[10px] font-semibold">Backdated</div>}
          {(v.changes || []).map(c => <div key={c} className="text-slate-500">• {c}</div>)}
          {(v.recalculated || []).length > 0 && (
            <div className="text-amber-700 mt-0.5" data-testid={`isa-rules-version-${v.version}-recalc`}>
              Recalculated {v.recalculated.length} saved day(s): {v.recalculated.map(x => `${x.date} ₹${x.old_net}→₹${x.new_net}`).join(', ')}</div>
          )}
        </div>
      ))}
    </div>
  </Card>
);

export const IsaRulesTab = () => {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [opts, setOpts] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [eff, setEff] = useState('');
  const load = useCallback(() => api.get('/inside-sales-audit/config').then(r => {
    setD(r.data); setEff(r.data.effective_next);
    const base = r.data.upcoming || r.data.current;
    setForm({ rules: { ...base.rules }, employees: base.employees.map(e => ({ ...e })) });
    if (r.data.can_edit) api.get('/inside-sales-audit/crm-options').then(o => setOpts(o.data)).catch(() => toast.error('Could not load CRM users'));
  }), []);
  useEffect(() => { load(); }, [load]);

  if (!d || !form) return <div className="py-16 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const total = form.employees.reduce((n, e) => n + (parseFloat(e.share_pct) || 0), 0);
  const setEmp = (i, e) => setForm(f => ({ ...f, employees: f.employees.map((x, j) => (j === i ? e : x)) }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post('/inside-sales-audit/config', { ...form, note, effective_from: eff });
      const n = r.data.recalculated?.length || 0;
      toast.success(`Saved as version ${r.data.version} — applies from ${r.data.effective_from}${n ? ` · ${n} saved day(s) recalculated` : ''}`);
      setNote(''); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setBusy(false); }
  };
  const cur = d.current;

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="isa-rules-tab">
      <div className="lg:col-span-2 space-y-4">
        <Card className="p-4" data-testid="isa-rules-current">
          <h3 className="text-sm font-bold text-slate-900">In force today — version {cur.version} (since {cur.effective_from})</h3>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-xs">
            {Object.entries(d.labels).map(([k, l]) => <div key={k} className="flex justify-between"><span className="text-slate-500">{l}</span><b>{cur.rules[k]}</b></div>)}
            {Object.entries(d.mode_labels).map(([k, l]) => <div key={k} className="flex justify-between gap-2" data-testid={`isa-rules-current-${k}`}><span className="text-slate-500">{l}</span><b className="text-right">{d.modes[k][cur.rules[k]]}</b></div>)}
          </div>
          <div className="mt-3 text-xs text-slate-600">{cur.employees.map(e => `${e.name} (${e.emp_id}, owner ${e.owner_name}) ${e.share_pct}%`).join(' · ')}</div>
          {d.upcoming && (
            <p className="mt-3 text-xs bg-blue-50 text-blue-800 rounded px-3 py-2 inline-flex items-center gap-1.5" data-testid="isa-rules-upcoming">
              <CalendarClock className="w-3.5 h-3.5" />Version {d.upcoming.version} saved by {d.upcoming.created_by} starts {d.upcoming.effective_from}. Editing below replaces it.</p>
          )}
        </Card>

        {d.can_edit ? (
          <Card className="p-4 space-y-4" data-testid="isa-rules-form">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Change rules</h3>
              <p className="text-[11px] text-slate-500">By default changes apply from tomorrow. Pick an earlier date to recalculate saved days from then (submitted or approved months must be unlocked first).</p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs text-slate-600">Applies from
                <Input type="date" min={d.earliest} max={d.effective_next} className="h-8 text-xs mt-1 w-44" value={eff} onChange={e => setEff(e.target.value)} data-testid="isa-rules-effective-from" />
              </label>
              {eff && eff < d.effective_next && <p className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1.5" data-testid="isa-rules-backdate-warning">Backdated: every saved day from {eff} will be recalculated and the change is logged on each day.</p>}
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {Object.entries(d.mode_labels).map(([k, l]) => (
                <label key={k} className="text-xs text-slate-600">{l}
                  <select className={`${sel} mt-1`} value={form.rules[k]} onChange={e => setForm(f => ({ ...f, rules: { ...f.rules, [k]: e.target.value } }))} data-testid={`isa-rules-${k}`}>
                    {Object.entries(d.modes[k]).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </select>
                </label>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.entries(d.labels).map(([k, l]) => (
                <label key={k} className="text-xs text-slate-600">{l}
                  <Input type="number" min={0} className="h-8 text-xs mt-1" value={form.rules[k]} onChange={e => setForm(f => ({ ...f, rules: { ...f.rules, [k]: e.target.value } }))} data-testid={`isa-rules-${k}`} />
                </label>
              ))}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">Employees and payout split</span>
                <span className={`text-xs font-semibold ${Math.abs(total - 100) < 0.01 ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="isa-rules-pct-total">Total {total}% {Math.abs(total - 100) < 0.01 ? '' : '(must be 100%)'}</span>
              </div>
              {form.employees.map((e, i) => (
                <EmployeeRow key={i} e={e} i={i} opts={opts} onChange={x => setEmp(i, x)}
                  onRemove={() => setForm(f => ({ ...f, employees: f.employees.filter((_, j) => j !== i) }))} />
              ))}
              <Button size="sm" variant="outline" onClick={() => setForm(f => ({ ...f, employees: [...f.employees, { name: '', emp_id: '', owner_id: '', owner_name: '', share_pct: 0 }] }))} data-testid="isa-rules-add-emp">
                <Plus className="w-3.5 h-3.5 mr-1.5" />Add employee</Button>
            </div>
            <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} className="text-xs" placeholder="Reason for the change (required, recorded in the history)" data-testid="isa-rules-note" />
            <Button size="sm" onClick={save} disabled={busy} data-testid="isa-rules-save">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}Save — applies from {eff || d.effective_next}</Button>
          </Card>
        ) : <p className="text-xs text-slate-500" data-testid="isa-rules-readonly">Only an admin can change the rules.</p>}
      </div>
      <History history={d.history} />
    </div>
  );
};
